<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { CustomDataValueMatch, EchoFilterCheckbox } from '@/js/services/api/filter/types';
    import {
        MIN_QUERY_LENGTH,
        useSearchCustomDataValues,
    } from '@/js/services/api/filter/customDataValueQueries';
    import { useDebouncedRef } from '@/js/composables/useDebouncedRef';
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '../EchoButton.vue';
    import LoadingDots from '../LoadingDots.vue';

    const INITIAL_VISIBLE_RESULTS = 20;
    const DEBOUNCE_MS = 500;

    const props = defineProps<{
        customDataTypeId: string;
    }>();

    const filter = defineModel<EchoFilterCheckbox[]>('filter');

    const searchText = ref('');
    const trimmedSearchText = computed(() => searchText.value.trim());
    const debouncedQuery = useDebouncedRef(trimmedSearchText, DEBOUNCE_MS);
    const showAll = ref(false);

    watch(debouncedQuery, () => {
        showAll.value = false;
    });

    const { data, isLoading, isPlaceholderData } = useSearchCustomDataValues(
        () => props.customDataTypeId,
        debouncedQuery
    );

    const options = computed(() => filter.value ?? []);
    const selectedOptions = computed(() => options.value.filter((option) => option.isSelected));
    const selectedNames = computed(
        () => new Set(selectedOptions.value.map((option) => option.name))
    );
    const hasQuery = computed(() => debouncedQuery.value.length >= MIN_QUERY_LENGTH);
    // keepPreviousData keeps the last results as placeholder after the query is disabled, so gate on the query too.
    const matches = computed(() => (hasQuery.value ? (data.value?.values ?? []) : []));
    const visibleMatches = computed(() =>
        showAll.value ? matches.value : matches.value.slice(0, INITIAL_VISIBLE_RESULTS)
    );
    const hiddenCount = computed(() => matches.value.length - visibleMatches.value.length);
    const visibleMatchNames = computed(
        () => new Set(visibleMatches.value.map((match) => match.value))
    );
    // Grouped below the results instead of merged into them, so ticking a result never moves its row.
    const otherSelectedOptions = computed(() =>
        selectedOptions.value.filter((option) => !visibleMatchNames.value.has(option.name))
    );

    const toCheckbox = (match: CustomDataValueMatch): EchoFilterCheckbox => ({
        id: match.id,
        name: match.value,
        isSelected: true,
        isDisabled: false,
        isGroup: false,
        isHidden: false,
        children: [],
    });

    const select = (match: CustomDataValueMatch) => {
        const existing = options.value.find((option) => option.name === match.value);

        filter.value = existing
            ? options.value.map((option) =>
                  option === existing ? { ...option, isSelected: true } : option
              )
            : [...options.value, toCheckbox(match)];
    };

    const deselect = (name: string) => {
        filter.value = options.value.map((option) =>
            option.name === name ? { ...option, isSelected: false } : option
        );
    };

    const toggle = (match: CustomDataValueMatch) => {
        if (selectedNames.value.has(match.value)) {
            deselect(match.value);
            return;
        }

        select(match);
    };
</script>

<template>
    <div class="mx-4 pb-4 mb-4 border-b border-b-gray-lightest">
        <div
            class="text-input border border-gray-dark custom-focus-within flex flex-wrap items-center overflow-hidden p-1"
        >
            <input
                v-model="searchText"
                :placeholder="__('search')"
                :aria-label="__('search')"
                type="text"
                class="border-0 focus:ring-0 flex-grow py-0.5 px-1.5 min-w-24 w-24"
            />
        </div>

        <p v-if="!hasQuery" class="pt-2 text-caption">
            {{ __('filter-search-min-characters', { count: String(MIN_QUERY_LENGTH) }) }}
        </p>
        <p
            v-else-if="!isLoading && !isPlaceholderData && matches.length === 0"
            class="pt-2 text-caption"
        >
            {{ __('no-results-found-change-query') }}
        </p>
    </div>
    <div class="-ml-1.5 pr-4 pb-5">
        <div v-if="isLoading" class="pl-5.5 py-1">
            <LoadingDots />
        </div>
        <template v-else>
            <!-- Stale matches stay on screen while the next request runs to avoid a layout collapse, dimmed and inert so none can be ticked. -->
            <ul class="pl-5.5" :class="{ 'opacity-50': isPlaceholderData }">
                <li v-for="match in visibleMatches" :key="match.id">
                    <label class="flex items-start py-1 text-body">
                        <input
                            type="checkbox"
                            class="Custom-checkbox"
                            :checked="selectedNames.has(match.value)"
                            :data-name="match.value"
                            :disabled="isPlaceholderData"
                            @change="toggle(match)"
                        />
                        <span>{{ match.value }}</span>
                    </label>
                </li>
            </ul>
            <div v-if="hiddenCount > 0" class="pl-5.5 pt-2 mb-4">
                <EchoButton size="small" @click="showAll = true">
                    {{ __('show-all-count', { count: String(matches.length) }) }}
                </EchoButton>
            </div>
        </template>
        <ul v-if="otherSelectedOptions.length > 0" class="pl-5.5">
            <li v-for="option in otherSelectedOptions" :key="option.name">
                <label class="flex items-start py-1 text-body">
                    <input
                        type="checkbox"
                        class="Custom-checkbox"
                        :checked="true"
                        :data-name="option.name"
                        @change="deselect(option.name)"
                    />
                    <span>{{ option.name }}</span>
                </label>
            </li>
        </ul>
    </div>
</template>
